/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import { Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import { useAuth } from "Authentication/AuthProvider";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { mvpProgramProfileStatuses } from "Constants/ProfileConstants";
import {
  fetchProgramProfile,
  updateRDProgramProfile,
} from "Dataflows/RD/UserProfile/RDUserProfileAction";
import {
  getProgramProfileUpdateStatus,
  getUpdateProgramProfileErrorMessage,
} from "Dataflows/RD/UserProfile/RDUserProfileSelector";

import { IRegionalDirectorProgramProfileEditFormProps } from "./IRegionalDirectorProgramProfileEditFormProps";

export const RegionalDirectorProgramProfileEditForm = (
  props: IRegionalDirectorProgramProfileEditFormProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, defaultValues } = props;
  const { account } = useAuth();

  const updateProgramProfileOperationStatus = useSelector(getProgramProfileUpdateStatus);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(
      Yup.object().shape({
        programProfileStatus: Yup.string().required("Program Profile Status is required"),
      })
    ),
  });

  const dispatch = useDispatch();
  const programProfileUpdateErrorMessage = useSelector(getUpdateProgramProfileErrorMessage);

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    reset();
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((data) => {
      if (defaultValues.userProfileId && data.programProfileStatus) {
        return dispatch(
          updateRDProgramProfile(
            defaultValues.userProfileId,
            data.programProfileStatus,
            account.userName
          )
        );
      }
    });

  useEffect(() => {
    if (updateProgramProfileOperationStatus === OperationStatus.Succeeded) {
      dispatch(fetchProgramProfile(defaultValues.userProfileId as number));
      dismissPanel();
    }
  }, [updateProgramProfileOperationStatus === OperationStatus.Succeeded]);

  const isSaving = updateProgramProfileOperationStatus === OperationStatus.Pending;

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Edit Program Details"
        saveButtonLabel="Save"
        error={programProfileUpdateErrorMessage}
        disableActions={isSaving}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <ComboBoxController
              control={control}
              errors={errors}
              inputName="programProfileStatus"
              isRequired={true}
              labelDisplayName="Program Profile Status"
              options={mvpProgramProfileStatuses}
              isDisabled={isSaving}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
